import { referralRewards } from '../../utils/referral-rewards';

/*
 * The daily question allowance, and the words for the moment a student uses it up.
 *
 * How QLearn-service counts it (QuizManager.getQuestionsFromSelectedCategories and
 * QuizDailyLimitGuard, which share the same inputs):
 * - limit: questionsPerDay of the student's first selected plan;
 * - used: distinct question ids in the quizzes the student CREATED since 00:00 UTC
 *   (QuizService.findQuizQuestionByUserIdToday queries createDt >= LocalDate.now(UTC);
 *   the prod JVM runs in UTC, so that bound is midnight UTC). Tutor-assigned
 *   quizzes and the one-tap onboarding seed don't count.
 * - left = max(limit - used, 0), reported as questionLeftInDay.
 *
 * So the allowance comes back at the next 00:00 UTC, not at the student's own
 * midnight: 8 PM the same evening in New York (EDT), 5 PM in California. "Tomorrow"
 * would be wrong for most US students, so the copy names the local clock time.
 *
 * Tutors and admins are uncapped and the count reports questionLeftInDay 0 for
 * them, so callers only read this for students.
 */

// Same count request body as the QLink drawer's allowance read (utils/qlink-free-allowance.js).
export const COUNT_QUESTION_OPTIONS = Object.freeze({ unused: true, correct: false, incorrect: false, marked: false });

const HOUR_MS = 60 * 60 * 1000;

/** The next 00:00 UTC after `now`: when the server's daily tally starts again. */
export const nextDailyReset = (now = new Date()) =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));

const calendarDay = (date, timeZone) => new Intl.DateTimeFormat('en-US', {
  timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

/**
 * When the next set unlocks, in the student's own clock: "today at 8:00 PM",
 * "tomorrow at 5:30 AM", or "within the hour". `timeZone` and `locale` default to
 * the device's; tests pin them.
 */
export const resetPhrase = (now = new Date(), { timeZone, locale } = {}) => {
  const reset = nextDailyReset(now);
  const msLeft = reset.getTime() - now.getTime();
  if (msLeft <= HOUR_MS) {
    return 'within the hour';
  }
  try {
    const time = new Intl.DateTimeFormat(locale, { timeZone, hour: 'numeric', minute: '2-digit' })
      .format(reset)
      // Newer ICU puts a narrow no-break space before AM/PM.
      .replace(/\u202f/g, ' ');
    // The reset is always less than 24 hours away, so it falls on the student's
    // today or their tomorrow.
    const day = calendarDay(reset, timeZone) === calendarDay(now, timeZone) ? 'today' : 'tomorrow';
    return `${day} at ${time}`;
  } catch {
    const hours = Math.round(msLeft / HOUR_MS);
    return `in about ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
};

/**
 * Reads a count response (POST /quiz/categories/questions) and the student's
 * selected plans (GET /user/{id}/plan/true).
 *
 * @returns null when the count carries no usable allowance; otherwise
 *   { left, limit, exhausted, free, exam } where
 *   - limit is the plan's daily allowance, or null when the response doesn't say;
 *   - free is true only for a single selected $0 plan whose daily allowance is
 *     the limit (the same rule as the QLink drawer's "N free questions"), false
 *     for any other known plan set, and null when the plans are unknown;
 *   - exam is `exam` when that single plan covers it, else null.
 */
export const resolveDailyLimit = ({ count, plans, exam = null } = {}) => {
  const left = count?.questionLeftInDay;
  if (!Number.isInteger(left) || left < 0) {
    return null;
  }
  const rawLimit = count?.questionInDayLimit;
  const limit = Number.isInteger(rawLimit) && rawLimit >= 0 && left <= rawLimit ? rawLimit : null;
  const plansKnown = Array.isArray(plans);
  const plan = plansKnown && plans.length === 1 ? plans[0] : null;
  let free = null;
  if (plansKnown) {
    free = Boolean(plan) && plan.price === 0 && Number.isInteger(limit) && limit > 0
      && plan.questionsPerDay === limit;
  }
  const coversExam = Boolean(exam) && Array.isArray(plan?.examTypes) && plan.examTypes.includes(exam);
  return { left, limit, exhausted: left === 0, free, exam: coversExam ? exam : null };
};

const questions = (n) => (n === 1 ? 'question' : 'questions');

/**
 * Title and body for a used-up allowance. Paid and unknown plans get the same
 * plan-neutral words: only a confirmed free plan is told about "free" questions.
 */
export const limitCopy = ({ limit, free = false, exam = null, when }) => {
  if (limit === 0) {
    return {
      title: 'Your plan has no daily questions',
      body: 'See plans to keep practicing.',
    };
  }
  if (!Number.isInteger(limit)) {
    return {
      title: "Today's questions: done\u00a0✓",
      body: `Your daily questions reset ${when}.`,
    };
  }
  const kind = free ? 'free ' : '';
  const examWord = exam ? `${exam} ` : '';
  const next = limit === 1
    ? `Your next ${kind}${examWord}question unlocks ${when}.`
    : `Your next ${limit} ${kind}${examWord}questions unlock ${when}.`;
  return {
    title: `Today's ${limit} ${kind}${questions(limit)}: done\u00a0✓`,
    body: next,
  };
};

/**
 * Create Quiz's hint under the Create button once today's allowance is used up.
 * The count's eligibleQuestionCount is 0 then, which used to read as "No questions
 * match your current selections" and sent students off changing filters (each
 * change re-opened the out-of-questions modal). Empty for tutors and admins (the
 * count reports 0 left for them although they are uncapped) and whenever
 * questions are left.
 */
export const allowanceUsedHint = (count, role, now = new Date()) => {
  if (role === 'ROLE_ADMIN' || role === 'ROLE_TUTOR' || count?.questionLeftInDay !== 0) {
    return '';
  }
  if (count?.questionInDayLimit === 0) {
    return 'Your plan has no daily questions. See plans to keep practicing.';
  }
  return `You've used today's questions. More unlock ${resetPhrase(now)}.`;
};

/** The results page's next-step line while questions are left today. */
export const allowanceLeftLine = ({ left, limit }) => (
  Number.isInteger(limit) && limit >= left
    ? `You have ${left} of ${limit} ${questions(limit)} left today.`
    : `You have ${left} ${questions(left)} left today.`
);

// The native paywall says "1 week", never a raw day count (subscriptions-list.jsx).
const NATIVE_TRIAL_LENGTH = { 7: '1 week', 14: '2 weeks', 30: '1 month' };

export const trialCtaLabel = ({ days, native = false }) => {
  const length = native && NATIVE_TRIAL_LENGTH[days]
    ? NATIVE_TRIAL_LENGTH[days]
    : `${days} ${days === 1 ? 'day' : 'days'}`;
  return `Try Pro free for ${length}`;
};

/**
 * The web trial length the paywall will show: the trialPeriodDays of the paid
 * plans that carry a trial, preferring plans that cover `exam`. More than one
 * length (or none) means there is no single number to promise, so null.
 */
export const trialDaysFromPlans = (plans, exam = null) => {
  const withTrial = (Array.isArray(plans) ? plans : []).filter((plan) => plan
    && Number(plan.price) > 0
    && Number.isInteger(plan.trialPeriodDays) && plan.trialPeriodDays > 0);
  const covering = exam
    ? withTrial.filter((plan) => Array.isArray(plan.examTypes) && plan.examTypes.includes(exam))
    : [];
  const pool = covering.length ? covering : withTrial;
  const lengths = [...new Set(pool.map((plan) => plan.trialPeriodDays))];
  return lengths.length === 1 ? lengths[0] : null;
};

/**
 * The one web plan "Try Pro free" should open at checkout: the paid trial plan
 * that covers `exam` (with no exam given, the only paid trial plan). When the
 * exam is known but no trial plan covers it, or more than one candidate is left,
 * this returns null and the button opens the full plan list as before, so a
 * student is never dropped onto another exam's plan.
 */
export const trialPlanIdFromPlans = (plans, exam = null) => {
  const withTrial = (Array.isArray(plans) ? plans : []).filter((plan) => plan
    && plan.id
    && Number(plan.price) > 0
    && Number.isInteger(plan.trialPeriodDays) && plan.trialPeriodDays > 0);
  const pool = exam
    ? withTrial.filter((plan) => Array.isArray(plan.examTypes) && plan.examTypes.includes(exam))
    : withTrial;
  return pool.length === 1 ? pool[0].id : null;
};

/**
 * Where a "see plans" button goes. With a plan id, /subscriptions opens that
 * plan's review step ("Due today: $0.00") once; without one, the plan list.
 */
export const plansLocation = (planId = null) => (
  // Only a real id: a click event passed by mistake must never become route state.
  typeof planId === 'string' && planId
    ? { pathname: '/subscriptions', state: { preselectPlanId: planId } }
    : '/subscriptions'
);

/**
 * "Share & Earn Free Pro" only for a free student while the server says the
 * inviter reward is on (the same rule as the invite prompt); otherwise a plain
 * invite, so a paying student is never promised free Pro.
 */
export const shareCtaLabel = ({ free, stats }) => (
  free === true && referralRewards(stats).inviterDays !== null ? 'Share & Earn Free Pro' : 'Invite a friend'
);

/**
 * "Email me tomorrow's questions" stays hidden until the daily practice email
 * exists (a separate QLearn-service change). Off unless the build sets
 * REACT_APP_PRACTICE_EMAIL_OPTIN_ENABLED=true. Read at call time so tests can
 * flip it.
 */
export const practiceEmailOptInEnabled = () => process.env.REACT_APP_PRACTICE_EMAIL_OPTIN_ENABLED === 'true';
